/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRecommendationsExplainerEnabled } from '@coursera-web/recommendations-common';
import type { RecommendationsExplainerInput } from '@coursera-web/recommendations-common';

import RecommendationsExplainerButton from 'bundles/recommendations-explainer/components/RecommendationsExplainerButton';

const styles = {
  titleRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    position: relative;
    z-index: 1;

    & > :first-child {
      flex: 0 1 auto;
      width: fit-content;
      min-width: 0;
    }
  `,
};

export type RecommendationsExplainerTitleRowProps = {
  /** The collection to explain. When nullish, or the explainer is disabled, only `children` render. */
  recommendationsExplainer?: RecommendationsExplainerInput | null;
  /** The title content, rendered before the explainer button. */
  children: React.ReactNode;
} & Pick<React.ComponentProps<'div'>, 'className'>;

/**
 * Lays out a title next to its `RecommendationsExplainerButton`. When the explainer is unavailable it renders just
 * `children`, with no wrapper element, so it does not change the layout for learners.
 *
 * @example
 * <RecommendationsExplainerTitleRow recommendationsExplainer={collection.recommendationsExplainer}>
 *   <Typography variant="h2semibold">{title}</Typography>
 * </RecommendationsExplainerTitleRow>
 */
const RecommendationsExplainerTitleRow = ({
  recommendationsExplainer,
  children,
  ...rowProps
}: RecommendationsExplainerTitleRowProps) => {
  const enabled = useRecommendationsExplainerEnabled();

  if (!enabled || !recommendationsExplainer) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <div {...rowProps} css={styles.titleRow}>
      {children}
      <RecommendationsExplainerButton {...recommendationsExplainer} />
    </div>
  );
};

export default RecommendationsExplainerTitleRow;
